/**
 * JobDetailDrawer
 * Drawer full-width (90vw) para el detalle completo de un job completado.
 * Recibe parsedResult descargado por el modal (cero re-fetch, ADR-3/ADR-6).
 */
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Chip,
  Alert,
} from "@mui/material";
import { Close as CloseIcon, Schedule as ScheduleIcon, PersonOutline as PersonIcon } from "@mui/icons-material";
import type {
  JobDisplayData,
  BookingReconciliationResultDto,
  AssignContractLinesResultDto,
  EligibleInvoicesResultDto,
} from "@/types/jobs";
import {
  JOB_TYPE_DESIGN,
  MONO_SX,
  CHIP_GEOMETRIC_SX,
} from "./jobs-design-tokens";
import {
  JOB_TYPE_LABEL,
  STATUS_LABEL,
  STATUS_COLOR,
  formatDateTime,
} from "./JobDetailDialog";
import { ReconciliationDetailPanel } from "./detail-panels/ReconciliationDetailPanel";
import { ContractAssignmentDetailPanel } from "./detail-panels/ContractAssignmentDetailPanel";
import { EligibleInvoicesDetailPanel } from "./detail-panels/EligibleInvoicesDetailPanel";

// ─── Props ────────────────────────────────────────────────────────────────────

export interface JobDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  job: JobDisplayData;
  parsedResult: unknown;
}

// ─── Header ───────────────────────────────────────────────────────────────────

function DrawerHeader({ job, onClose }: { job: JobDisplayData; onClose: () => void }) {
  const typeDesign = JOB_TYPE_DESIGN[job.jobType];

  return (
    <Box
      sx={{
        px: 3.5,
        pt: 2.5,
        pb: 2,
        background: typeDesign?.gradient ?? "none",
        borderBottom: `2px solid ${typeDesign?.primary ?? "#e0e0e0"}`,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
          {/* Title row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: typeDesign?.primary ?? "#94a3b8",
                flexShrink: 0,
              }}
            />
            <Typography variant="h6" fontWeight={700} sx={{ color: typeDesign?.text ?? "text.primary", fontSize: "1.05rem" }}>
              {JOB_TYPE_LABEL[job.jobType] ?? job.jobType}
            </Typography>
            <Chip
              label={STATUS_LABEL[job.status] ?? job.status}
              color={STATUS_COLOR[job.status] ?? "default"}
              size="small"
              sx={{ fontWeight: 600, height: 22, fontSize: "0.72rem", ...CHIP_GEOMETRIC_SX }}
            />
          </Box>

          {/* Metadata grid */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap" }}>
            <Typography sx={{ ...MONO_SX, color: "text.secondary" }}>
              Job <Box component="span" fontWeight={700}>#{job.id}</Box>
            </Typography>
            <Typography sx={{ ...MONO_SX, color: "text.secondary" }}>
              Entidad <Box component="span" fontWeight={700}>#{job.entityId}</Box>
            </Typography>

            {job.requestedBy && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                <Typography variant="caption" color="text.secondary">{job.requestedBy}</Typography>
              </Box>
            )}

            {(job.startedAt || job.completedAt) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                <Typography variant="caption" color="text.secondary">
                  {job.startedAt && formatDateTime(job.startedAt)}
                  {job.startedAt && job.completedAt && " → "}
                  {job.completedAt && formatDateTime(job.completedAt)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: 0.25, flexShrink: 0, color: "text.disabled" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

// ─── Panel router ─────────────────────────────────────────────────────────────

function PanelRouter({ job, parsedResult }: { job: JobDisplayData; parsedResult: unknown }) {
  if (!parsedResult) {
    return (
      <Alert severity="warning">
        No hay datos de resultado disponibles para este job.
      </Alert>
    );
  }

  switch (job.jobType) {
    case "BookingReconciliation":
      return (
        <ReconciliationDetailPanel result={parsedResult as BookingReconciliationResultDto} />
      );
    case "ContractAssignment":
      return (
        <ContractAssignmentDetailPanel result={parsedResult as AssignContractLinesResultDto} />
      );
    case "EligibleInvoicesReport":
      return (
        <EligibleInvoicesDetailPanel
          result={parsedResult as EligibleInvoicesResultDto}
          jobId={job.id}
          service={job.service}
        />
      );
    default:
      return (
        <Alert severity="info">
          No hay panel de detalle disponible para el tipo "{job.jobType}".
        </Alert>
      );
  }
}

// ─── Drawer ───────────────────────────────────────────────────────────────────

export function JobDetailDrawer({ open, onClose, job, parsedResult }: JobDetailDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", md: "75vw" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DrawerHeader job={job} onClose={onClose} />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          p: 3.5,
          display: "flex",
          flexDirection: "column",
          bgcolor: "rgba(0,0,0,0.01)",
        }}
      >
        <PanelRouter job={job} parsedResult={parsedResult} />
      </Box>
    </Drawer>
  );
}
